import _ from "lodash";
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { EmptyState } from "@toddle-edu/ds-web";
import {
  NoMessagesIllustration,
  NoFilterResultIllustration,
  NoSearchInFilterIllustration,
  NoSearchResultsIllustration,
  AddClassIllustration,
  AllCompletedIllustration,
} from "@toddle-edu/ds-theme";

import { useI18n } from "Hooks";
import { STAFF, STUDENT, PARENT } from "Constants/stringConstants";
import {
  sortUserTypes,
  isMessagingRevampV2Enabled,
} from "MessagingV2/modules/Utils";
import { useDispatch } from "Hooks/useDispatch";

import classes from "./EmptyChatList.scss";
import { setRevampV2FilterSearch } from "MessagingV2/modules/MessagingModule";
import CreateDepartmentChannelButton from "../../Channels/CreateDepartmentChannel";

export const VARIANTS = {
  NO_FILTERS: "NO_FILTERS",
  APPLIED_FILTERS: "APPLIED_FILTERS",
  SEARCH: "SEARCH",
  SEARCH_WITHOUT_INPUT: "SEARCH_WITHOUT_INPUT",
  PUBLIC_CHANNEL: "PUBLIC_CHANNEL",
  DEPARTMENT_CHANNEL: "DEPARTMENT_CHANNEL",
  DEPARTMENT_CHANNEL_STAFF: "DEPARTMENT_CHANNEL_STAFF",
  UNREAD: "UNREAD",
};

const ENTITY_LOCALE_MAP = {
  [STAFF]: "common:staff_members_plural_lowercase",
  [STUDENT]: "common:student_lowercase_other",
  [PARENT]: "common:family_member_plural_lowercase",
};

const EmptyChatList = ({ variant, userTypeFilters, searchText }) => {
  const { t } = useI18n(["common"]);

  const sortedFilters = useMemo(() => sortUserTypes([...userTypeFilters]), [
    userTypeFilters,
  ]);
  const dispatch = useDispatch();

  const onResetSearchClick = () => dispatch(setRevampV2FilterSearch(""));

  const isUIRevampV2Enabled = isMessagingRevampV2Enabled();
  const data = {
    [VARIANTS.NO_FILTERS]: {
      illustration: NoMessagesIllustration,
      title: t("common:no_conversations_yet"),
      subtitle: t("common:click_on_plus_to_start_a_new_chat"),
    },
    [VARIANTS.APPLIED_FILTERS]: {
      illustration: NoFilterResultIllustration,
      title: t("common:noResultHeading"),
      subtitle: t("common:no_conversation_with_label", {
        label: t("common:join_array_with_and", {
          array: _.map(sortedFilters, userType =>
            t(ENTITY_LOCALE_MAP[userType])
          ),
        }),
      }),
    },
    [VARIANTS.SEARCH]: {
      illustration: NoSearchResultsIllustration,
      title: isUIRevampV2Enabled
        ? t("common:no_chat_channel_found_label", {
            label: searchText,
          })
        : t("common:noResultHeading"),
      subtitle: isUIRevampV2Enabled
        ? t("common:adjust_search_and_try_again")
        : t("common:no_search_results_found_subtext_with_label_short", {
            label: searchText,
          }),
    },
    [VARIANTS.SEARCH_WITHOUT_INPUT]: {
      illustration: NoSearchResultsIllustration,
      title: t("common:search_a_keyword"),
      subtitle: t("common:messaging_empty_search_placeholder_text"),
    },
    [VARIANTS.PUBLIC_CHANNEL]: {
      illustration: NoSearchInFilterIllustration,
      title: t("common:public_channel_empty_joined"),
      subtitle: t("common:click_on_button_below_to_browse_channels"),
    },
    [VARIANTS.DEPARTMENT_CHANNEL]: {
      illustration: AddClassIllustration,
      title: t("common:department_channel_empty"),
      subtitle: t("common:department_channel_empty_subtext"),
      primaryButton: <CreateDepartmentChannelButton />,
    },
    [VARIANTS.DEPARTMENT_CHANNEL_STAFF]: {
      illustration: AddClassIllustration,
      title: t("common:no_department_created"),
      subtitle: t("common:department_channel_empty_staff_subtext"),
    },
    [VARIANTS.UNREAD]: {
      illustration: AllCompletedIllustration,
      title: t("common:you_are_all_caught_up"),
      subtitle: t("common:you_dont_have_any_unread_conversations_right_now"),
    },
  };

  return (
    <div
      className={classNames(classes.container, {
        [classes.containerV2]: isMessagingRevampV2Enabled(),
      })}
    >
      <EmptyState
        dsVersion="2.0"
        size="small"
        {..._.get(data, variant, data[VARIANTS.NO_FILTERS])}
      />
    </div>
  );
};

EmptyChatList.propTypes = {
  variant: PropTypes.oneOf(_.values(VARIANTS)).isRequired,
  // userTypeFilters is required if variant is APPLIED_FILTERS
  userTypeFilters: PropTypes.arrayOf(PropTypes.string),
  // searchText is required if variant is SEARCH
  searchText: PropTypes.string,
};

EmptyChatList.defaultProps = {
  userTypeFilters: [],
  searchText: "",
};

export default EmptyChatList;
